import { useMemo } from "react";
import { isString, isUndefined } from "lodash";
import { Controller } from "react-hook-form";
import type { PathArray } from "common/utils/formStructure";
import TextareaInput from "../../../../../../Inputs/Textarea";
import {
  getSchemaPropertyFormPath,
  getUiSchemaPropertyFormPath,
  useBuilderFormContext,
  useBuilderWatch,
} from "../../form/form";
import { FieldWrapper } from "../__styles__/SidebarColumn";
import { WidgetSettingsContainer } from "./__styles__/WidgetSettings";
import type { InputSettingsFormProps } from "./FieldSettingsView";
import { useIsFieldRequired } from "./required";
import { DefaultValuesSection } from "./shared/DefaultValuesSection";
import { DisplaySettings } from "./shared/DisplaySettings";
import { FieldPermissionsConfig } from "./shared/FieldPermissionsConfig";
import { FieldSettingsConfig } from "./shared/FieldSettingsConfig";
import { GenerativeAIPromptInput } from "./shared/GenerativeAIPromptInput";
import { HelperTextInput } from "./shared/HelperTextInput";
import { PlaceholderTextInput } from "./shared/PlaceholderTextInput";
import { TextDynamicDefaultInput } from "./shared/TextDynamicDefaultInput";

export function TextareaWidgetSettings({
  fieldPath,
  isReadOnly,
  isModuleInput,
}: InputSettingsFormProps) {
  const { setValue } = useBuilderFormContext();
  const isFormulaField = !!useBuilderWatch(
    getSchemaPropertyFormPath(fieldPath, "formula"),
  );

  const useGenerativeAIPropertyFormPath = getUiSchemaPropertyFormPath(
    fieldPath,
    "useGenerativeAI",
  );

  const isRequired = useIsFieldRequired(fieldPath);

  return (
    <WidgetSettingsContainer>
      <FieldSettingsConfig
        fieldPath={fieldPath}
        isModuleInput={isModuleInput}
        isFormulaField={isFormulaField}
        isReadOnly={isReadOnly}
        onRequiredChange={e => {
          if (e.target.checked) {
            setValue(useGenerativeAIPropertyFormPath, false, {
              shouldValidate: true,
            });
          }
        }}
      />
      <HelperTextInput fieldPath={fieldPath} isReadOnly={isReadOnly} />
      <PlaceholderTextInput fieldPath={fieldPath} isReadOnly={isReadOnly} />
      {!isFormulaField && (
        <GenerativeAIPromptInput
          fieldPath={fieldPath}
          isReadOnly={isReadOnly}
          isRequired={isRequired}
        />
      )}
      <DefaultValuesSection showTooltip>
        <FieldWrapper data-testid="dynamic-default-value-wrapper">
          <TextDynamicDefaultInput
            fieldPath={fieldPath}
            isReadOnly={isReadOnly}
          />
        </FieldWrapper>
        <FieldWrapper data-testid="static-default-value-wrapper">
          <TextareaStaticDefaultInput
            fieldPath={fieldPath}
            isReadOnly={isReadOnly}
          />
        </FieldWrapper>
      </DefaultValuesSection>
      <FieldPermissionsConfig fieldPath={fieldPath} isReadOnly={isReadOnly} />
      <DisplaySettings fieldPath={fieldPath} isReadOnly={isReadOnly} />
    </WidgetSettingsContainer>
  );
}

type TextareaStaticDefaultInputProps = {
  fieldPath: PathArray;
  isReadOnly?: boolean;
};

function TextareaStaticDefaultInput({
  fieldPath,
  isReadOnly,
}: TextareaStaticDefaultInputProps) {
  const { control } = useBuilderFormContext();

  const defaultValuePropertyFormPath = getUiSchemaPropertyFormPath(
    fieldPath,
    "defaultValue",
  );
  const defaultValue = useBuilderWatch(defaultValuePropertyFormPath);

  const value = useMemo<string | null>(() => {
    if (isString(defaultValue) || isUndefined(defaultValue)) {
      return defaultValue || "";
    }
    throw new Error("defaultValue must be a string or undefined");
  }, [defaultValue]);

  return (
    <Controller
      control={control}
      name={defaultValuePropertyFormPath}
      render={({ field }) => {
        return (
          <TextareaInput
            name={defaultValuePropertyFormPath}
            value={value}
            label="Static default value"
            onChange={value => {
              // store no value (undefined) for ui:defaultValue instead of empty
              // string in form state
              field.onChange(value || undefined);
            }}
            disabled={isReadOnly}
          />
        );
      }}
    />
  );
}
